import { c as _c } from "react/compiler-runtime";
import { useDropLinkPreviewToggleControl } from "@/components/waves/drops/useDropLinkPreviewToggleControl";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import React from "react";
import QuorumProposalCompactContent from "@/components/waves/quorum/QuorumProposalCompactContent";
import { parseQuorumProposalMarkdown } from "@/components/waves/quorum/quorumProposalMarkdown";
import type { DropContentPresentation } from "./dropContentPresentation";
import EditDropLexical from "./EditDropLexical";
import WaveDropQuoteWithDropId from "./WaveDropQuoteWithDropId";
interface WaveDropPartContentMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedGroups?: Array<ApiDropGroupMention> | undefined;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly part: ApiDropPart;
  readonly wave: ApiWaveMin;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined; // Add drop to check for edited status
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const WaveDropPartContentMarkdown: React.FC<WaveDropPartContentMarkdownProps> = t0 => {
  const $ = _c(62);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    part,
    wave,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    onLinkCardActionsActiveChange,
    contentPresentation: t4,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const mentionedGroups = t5;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const linkPreviewToggleControl = useDropLinkPreviewToggleControl(drop);
  const dropId = drop?.id;
  const dropSerialNo = drop?.serial_no;
  const waveId = wave.id;
  let t6;
  bb0: {
    let t7;
    if ($[2] !== embedPath) {
      t7 = embedPath ? [...embedPath] : [];
      $[2] = embedPath;
      $[3] = t7;
    } else {
      t7 = $[3];
    }
    const path = t7;
    if (!dropId || path.includes(dropId)) {
      t6 = path;
      break bb0;
    }
    let t8;
    if ($[4] !== dropId || $[5] !== path) {
      t8 = [...path, dropId];
      $[4] = dropId;
      $[5] = path;
      $[6] = t8;
    } else {
      t8 = $[6];
    }
    t6 = t8;
  }
  const currentDropEmbedPath = t6;
  let t7;
  if ($[7] !== dropSerialNo || $[8] !== quotePath || $[9] !== waveId) {
    bb1: {
      const path_0 = quotePath ? [...quotePath] : [];
      if (dropSerialNo === undefined) {
        t7 = path_0;
        break bb1;
      }
      const currentQuoteKey = `${waveId}:${dropSerialNo}`;
      if (!path_0.includes(currentQuoteKey)) {
        path_0.push(currentQuoteKey);
      }
      t7 = path_0;
    }
    $[7] = dropSerialNo;
    $[8] = quotePath;
    $[9] = waveId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const currentQuotePath = t7;
  let t8;
  if ($[11] !== contentPresentation || $[12] !== part.content) {
    t8 = contentPresentation === "quorumCompact" ? parseQuorumProposalMarkdown(part.content) : null;
    $[11] = contentPresentation;
    $[12] = part.content;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const compactProposal = t8;
  if (isEditing) {
    const t9 = part.content ?? "";
    const t10 = drop?.wave.authenticated_user_admin === true;
    let t11;
    if ($[14] !== onSave) {
      t11 = (content, mentions, groups, waves) => {
        if (onSave) {
          onSave(content, mentions, groups, waves);
        }
      };
      $[14] = onSave;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    let t12;
    if ($[16] !== onCancel) {
      t12 = () => {
        if (onCancel) {
          onCancel();
        }
      };
      $[16] = onCancel;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    let t13;
    if ($[18] !== isSaving || $[19] !== mentionedGroups || $[20] !== mentionedUsers || $[21] !== mentionedWaves || $[22] !== t10 || $[23] !== t11 || $[24] !== t12 || $[25] !== t9 || $[26] !== wave.id) {
      t13 = <EditDropLexical initialContent={t9} initialMentions={mentionedUsers} initialGroupMentions={mentionedGroups} initialWaveMentions={mentionedWaves} canMentionAll={t10} waveId={wave.id} isSaving={isSaving} onSave={t11} onCancel={t12} />;
      $[18] = isSaving;
      $[19] = mentionedGroups;
      $[20] = mentionedUsers;
      $[21] = mentionedWaves;
      $[22] = t10;
      $[23] = t11;
      $[24] = t12;
      $[25] = t9;
      $[26] = wave.id;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    return t13;
  }
  let t9;
  if ($[28] !== compactProposal || $[29] !== currentDropEmbedPath || $[30] !== currentQuotePath || $[31] !== drop?.hide_link_preview || $[32] !== drop?.id || $[33] !== drop?.nft_links || $[34] !== embedDepth || $[35] !== linkPreviewToggleControl || $[36] !== maxEmbedDepth || $[37] !== mentionedGroups || $[38] !== mentionedUsers || $[39] !== mentionedWaves || $[40] !== onLinkCardActionsActiveChange || $[41] !== onQuoteClick || $[42] !== part.content || $[43] !== referencedNfts) {
    t9 = compactProposal ? <QuorumProposalCompactContent proposal={compactProposal} mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /> : <DropPartMarkdownWithPropLogger mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} partContent={part.content} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} />;
    $[28] = compactProposal;
    $[29] = currentDropEmbedPath;
    $[30] = currentQuotePath;
    $[31] = drop?.hide_link_preview;
    $[32] = drop?.id;
    $[33] = drop?.nft_links;
    $[34] = embedDepth;
    $[35] = linkPreviewToggleControl;
    $[36] = maxEmbedDepth;
    $[37] = mentionedGroups;
    $[38] = mentionedUsers;
    $[39] = mentionedWaves;
    $[40] = onLinkCardActionsActiveChange;
    $[41] = onQuoteClick;
    $[42] = part.content;
    $[43] = referencedNfts;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== drop) {
    t10 = typeof drop?.updated_at === "number" && drop.updated_at !== drop.created_at && <div className="tw-mt-0.5 tw-text-[10px] tw-font-normal tw-leading-none tw-text-iron-500">(edited)</div>;
    $[45] = drop;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[47] = t10;
    $[48] = t9;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== currentDropEmbedPath || $[51] !== currentQuotePath || $[52] !== embedDepth || $[53] !== maxEmbedDepth || $[54] !== onLinkCardActionsActiveChange || $[55] !== onQuoteClick || $[56] !== part.quoted_drop || $[57] !== wave) {
    t12 = part.quoted_drop?.drop_id && <div className="tw-mt-1.5"><WaveDropQuoteWithDropId dropId={part.quoted_drop.drop_id} partId={part.quoted_drop.drop_part_id} maybeDrop={part.quoted_drop.drop ? {
        ...part.quoted_drop.drop,
        wave
      } : null} onQuoteClick={onQuoteClick} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={(embedDepth ?? 0) + 1} maxEmbedDepth={maxEmbedDepth} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /></div>;
    $[50] = currentDropEmbedPath;
    $[51] = currentQuotePath;
    $[52] = embedDepth;
    $[53] = maxEmbedDepth;
    $[54] = onLinkCardActionsActiveChange;
    $[55] = onQuoteClick;
    $[56] = part.quoted_drop;
    $[57] = wave;
    $[58] = t12;
  } else {
    t12 = $[58];
  }
  let t13;
  if ($[59] !== t11 || $[60] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[59] = t11;
    $[60] = t12;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  return t13;
};
export default WaveDropPartContentMarkdown;